// SPEC-HISTORY-PRIZE-SHORTNAME-AND-FONT-UNIFY-01 (D-199) ITEM_1: 履歴の景品名に短縮名を出すための
//   prize_id → short_name バッチ取得 + モジュールキャッシュ。履歴行ごとに問い合わせない(AC3)。
//   列名は short_name(prize_masters, 2489件中2486件=99.9%が充足)。prize_name_short は存在しない列であり
//   参照すると D-179 の出庫破壊と同種の事故になるため絶対に使わない。prize_id は TEXT 型。
import { supabase } from '../lib/supabase'
import { logger } from '../lib/logger'

// prize_id -> { short, full }(見つからない/空は null)。店舗を跨いで再利用(prize_id は一意)=実質「店舗単位でまとめてキャッシュ」。
//   D-1081: 正式名(prize_masters.prize_name)も同じ行で取って一緒に持つ。巡回の入力欄で「保存名がこのマスタの
//   景品の名前か」を確かめてから短縮名に置き換えるため。invalidatePrizeShortNames で両方まとめて落ちる。
const cache = new Map()

// 与えた prize_id 群のうち未キャッシュ分だけを 1 クエリでまとめ取得しキャッシュ。
// 返り値は既定で {prize_id: short_name|null}(D-199 からの形。呼び出し元はこの形を読む)。
// D-1081: { withPrizeName: true } のときだけ {prize_id: { shortName, prizeName }}(どちらも無ければ null)。
// 失敗時は取得分だけ返し巡回本体をブロックしない(短縮名が出ないだけ)。
export async function fetchPrizeShortNames(prizeIds, { withPrizeName = false } = {}) {
  const ids = [...new Set((prizeIds ?? []).filter(id => id != null && id !== ''))]
  const missing = ids.filter(id => !cache.has(id))
  if (missing.length) {
    try {
      // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
      const { data, error } = await supabase
        .from('prize_masters')
        .select('prize_id, short_name, prize_name')
        .in('prize_id', missing)
      if (error) throw error
      for (const id of missing) cache.set(id, null) // 既定 null(未登録=フル名フォールバック)
      for (const r of data ?? []) {
        if (r?.prize_id != null) cache.set(r.prize_id, { short: r.short_name ?? null, full: r.prize_name ?? null })
      }
    } catch (e) {
      logger.warn?.('ERR-PRIZE-SHORTNAME-FETCH', { count: missing.length, message: e?.message ?? String(e) })
    }
  }
  const out = {}
  for (const id of ids) {
    const hit = cache.get(id) ?? null
    out[id] = withPrizeName
      ? { shortName: hit?.short ?? null, prizeName: hit?.full ?? null }
      : (hit?.short ?? null)
  }
  return out
}

// SPEC-PRIZE-SHORTNAME-CACHE-INVALIDATE-01 (D-281): prize_masters.short_name を更新したら、その prize_id を
//   モジュール Map から落として次回 fetchPrizeShortNames で再取得させる。配列でも単一文字列でも受け、null/空は無視。
//   全件クリアはしない(他の景品のキャッシュを巻き添えに捨てない=1画面10クエリを避けるキャッシュ設計は維持)。
//   LIMIT: この無効化は同一端末・同一タブの Map にのみ効く。別端末/別タブで開いている巡回画面の Map には届かず、
//          そちらはアプリを開き直すまで古い名前が残る(今回はそこまで面倒を見ない)。
export function invalidatePrizeShortNames(prizeIds) {
  const list = Array.isArray(prizeIds) ? prizeIds : [prizeIds]
  for (const id of list) {
    if (id != null && id !== '') cache.delete(id)
  }
}

// テスト用: キャッシュ初期化。
export function _clearPrizeShortNameCache() { cache.clear() }
